import {
  apiGet,
  apiPatch,
  apiPut,
  postNdjsonStream,
} from "./client";
import type {
  AssistantCommandContext,
  AssistantCreditSettings,
  AssistantMessage,
  AssistantMessageFeedback,
  AssistantThread,
  StreamEvent,
} from "@ceo-cmd/shared";

export async function listAssistantThreads(): Promise<{
  threads: AssistantThread[];
}> {
  return apiGet("/api/assistant/threads");
}

export async function getAssistantThread(
  threadId: string,
  opts?: { startSeq?: number; limit?: number },
): Promise<{ thread: AssistantThread; messages: AssistantMessage[] }> {
  const q = new URLSearchParams();
  if (opts?.startSeq != null) q.set("startSeq", String(opts.startSeq));
  if (opts?.limit != null) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return apiGet(`/api/assistant/threads/${threadId}${qs ? `?${qs}` : ""}`);
}

export async function updateAssistantThreadStatus(
  threadId: string,
  status: "active" | "archived",
): Promise<{ thread: AssistantThread }> {
  return apiPatch(`/api/assistant/threads/${threadId}`, { status });
}

export async function setAssistantFeedback(
  threadId: string,
  messageId: string,
  feedback: AssistantMessageFeedback,
): Promise<{ message: AssistantMessage }> {
  return apiPatch(
    `/api/assistant/threads/${threadId}/messages/${messageId}/feedback`,
    { feedback },
  );
}

export async function getAssistantCreditSettings(): Promise<{
  settings: AssistantCreditSettings;
}> {
  return apiGet("/api/assistant/credit-settings");
}

export async function updateAssistantCreditSettings(
  settings: AssistantCreditSettings,
): Promise<{ settings: AssistantCreditSettings }> {
  return apiPut("/api/assistant/credit-settings", settings);
}

export type AssistantStreamMeta = {
  threadId?: string;
  messageId?: string;
  durationMs?: number;
  verb?: string;
  steps?: string[];
};

export async function sendAssistantMessage(
  body: {
    message: string;
    threadId?: string;
  } & AssistantCommandContext,
  onEvent: (event: StreamEvent, meta: AssistantStreamMeta) => void,
  signal?: AbortSignal,
): Promise<AssistantStreamMeta> {
  const meta: AssistantStreamMeta = {};
  const path = body.threadId
    ? `/api/assistant/threads/${body.threadId}/messages`
    : "/api/assistant";

  await postNdjsonStream(
    path,
    {
      message: body.message,
      portfolioId: body.portfolioId,
      companyId: body.companyId,
      companyName: body.companyName,
      commandItemId: body.commandItemId,
      title: body.title,
      recommendation: body.recommendation,
      situationSummary: body.situationSummary,
      whyNow: body.whyNow,
      expectedOutcome: body.expectedOutcome,
      riskOfInaction: body.riskOfInaction,
      relevantContact: body.relevantContact,
      evidence: body.evidence,
      financialExposure: body.financialExposure,
      alternatives: body.alternatives,
      ...(body.threadId ? {} : { threadId: body.threadId }),
    },
    (raw) => {
      const event = raw as StreamEvent;
      if (event.type === "status" && event.label === "thread" && event.threadId) {
        meta.threadId = event.threadId;
      }
      if (event.type === "status" && event.label === "message_saved") {
        if (event.messageId) meta.messageId = event.messageId;
        if (typeof event.durationMs === "number") meta.durationMs = event.durationMs;
        if (typeof event.verb === "string") meta.verb = event.verb;
        if (Array.isArray(event.steps)) meta.steps = event.steps;
      }
      onEvent(event, meta);
    },
    signal,
  );

  return meta;
}
